import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tent, Minus, Plus } from 'lucide-react';

/**
 * Kompakt «forleng opphold»-kontroll. Står som standard på 1 natt; brukeren kan
 * rulle antallet opp/ned med − / + før hen bekrefter. Selve midtknappen utfører
 * forlengelsen med gjeldende antall netter (regnet fra i dag).
 *
 * Ren presentasjon — dag-matematikk og lagring ligger i occupancyService/App.
 */
export interface ExtendStayControlProps {
  onExtend: (days: number) => void;
  maxDays?: number;
  /** `banner` = plass over kartet (mørk), `sheet` = i plass-arkets handlingsrad. */
  variant?: 'banner' | 'sheet';
}

const ExtendStayControl: React.FC<ExtendStayControlProps> = ({ onExtend, maxDays = 14, variant = 'sheet' }) => {
  const { t } = useTranslation();
  const [days, setDays] = useState(1);

  const dec = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDays(d => Math.max(1, d - 1));
  };
  const inc = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDays(d => Math.min(maxDays, d + 1));
  };
  const confirm = (e: React.MouseEvent) => {
    e.stopPropagation();
    onExtend(days);
  };

  const label = days === 1
    ? t('spot.occupied.extend_one', 'Forleng 1 natt')
    : t('spot.occupied.extend_other', { count: days, defaultValue: 'Forleng {{count}} netter' });

  const stepBtn = variant === 'banner'
    ? 'text-white/70 hover:text-white disabled:opacity-30 bg-white/10'
    : 'text-[var(--text-muted)] hover:text-[var(--text-primary)] disabled:opacity-30 bg-[var(--bg-input)] border border-[var(--border-primary)]';

  return (
    <div className="flex items-stretch gap-1 flex-1 min-w-0">
      <button
        onClick={dec}
        disabled={days <= 1}
        aria-label={t('spot.occupied.extend_fewer', 'Færre netter')}
        className={`min-w-11 min-h-11 px-2 rounded-2xl flex items-center justify-center active:scale-95 transition ${stepBtn}`}
      >
        <Minus size={16} strokeWidth={3} />
      </button>
      <button
        onClick={confirm}
        className="flex-1 min-w-0 min-h-11 px-3 rounded-2xl bg-emerald-500 text-white text-2xs font-black uppercase tracking-widest flex items-center justify-center gap-2 active:scale-95 transition shadow-lg shadow-emerald-500/25"
      >
        <Tent size={16} strokeWidth={3} className="shrink-0" />
        <span className="truncate">{label}</span>
      </button>
      <button
        onClick={inc}
        disabled={days >= maxDays}
        aria-label={t('spot.occupied.extend_more', 'Flere netter')}
        className={`min-w-11 min-h-11 px-2 rounded-2xl flex items-center justify-center active:scale-95 transition ${stepBtn}`}
      >
        <Plus size={16} strokeWidth={3} />
      </button>
    </div>
  );
};

export default ExtendStayControl;
